import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Skeleton,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Divider,
  Paper,
} from "@mui/material";
import { Add as AddIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useTranslation } from "i18n-fe";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useOrganizationMembers } from "../hooks/useOrganizationMembers";
import { useOrganizations } from "../hooks/useOrganizations";
import { PageLayout, StyledBreadcrumb, ConfirmButton } from "common-fe";
import { extractErrorMessage } from "../utils/errorUtils";

export default function OrganizationMembers() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { id: orgId } = useParams();
  const auth = useAuth();
  
  const {
    members,
    loading,
    error,
    addMember,
    removeMember,
    updateMemberPermissions,
    refetch
  } = useOrganizationMembers(orgId);

  const { organizations } = useOrganizations();
  const organization = organizations.find(org => org.id === orgId);

  // Separate state for add member form
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [addingMember, setAddingMember] = useState(false);
  const [addMemberError, setAddMemberError] = useState("");
  const [addMemberSuccess, setAddMemberSuccess] = useState("");

  // Separate state for members list operations
  const [membersListError, setMembersListError] = useState("");
  const [membersListSuccess, setMembersListSuccess] = useState("");

  // Handle adding a new member
  const handleAddMember = async () => {
    if (!newMemberEmail.trim()) {
      setAddMemberError(t("organizations.members.emailRequired"));
      setTimeout(() => setAddMemberError(""), 5000);
      return;
    }

    if (!newMemberEmail.includes('@')) {
      setAddMemberError(t("organizations.members.emailInvalid"));
      setTimeout(() => setAddMemberError(""), 5000);
      return;
    }

    try {
      setAddingMember(true);
      setAddMemberError("");
      setAddMemberSuccess("");
      await addMember(newMemberEmail.trim());
      setNewMemberEmail("");
      setAddMemberSuccess(t("organizations.members.addSuccess"));
      setTimeout(() => setAddMemberSuccess(""), 5000);
    } catch (err) {
      console.error("Failed to add member:", err);
      setAddMemberError(extractErrorMessage(err, t));
      setTimeout(() => setAddMemberError(""), 5000);
    } finally {
      setAddingMember(false);
    }
  };

  // Handle removing a member
  const handleRemoveMember = async (userId) => {
    try {
      setMembersListError("");
      setMembersListSuccess("");
      await removeMember(userId);
      setMembersListSuccess(t("organizations.members.removeSuccess"));
      setTimeout(() => setMembersListSuccess(""), 5000);
    } catch (err) {
      console.error("Failed to remove member:", err);
      setMembersListError(extractErrorMessage(err, t));
      setTimeout(() => setMembersListError(""), 5000);
    }
  };

  // Handle updating member permissions
  const handleUpdatePermissions = async (userId, isOwner) => {
    try {
      setMembersListError("");
      setMembersListSuccess("");
      await updateMemberPermissions(userId, isOwner);
      setMembersListSuccess(t("organizations.members.updateSuccess"));
      setTimeout(() => setMembersListSuccess(""), 5000);
    } catch (err) {
      console.error("Failed to update member permissions:", err);
      setMembersListError(extractErrorMessage(err, t));
      setTimeout(() => setMembersListError(""), 5000);
    }
  };

  // Handle going back
  const handleBack = () => {
    navigate("/orgs");
  };

  // Handle retry
  const handleRetry = () => {
    refetch();
  };

  const breadcrumbs = [
    <StyledBreadcrumb key="orgs" label={t("organizations.organizationsPageTitle")} onClick={handleBack} />,
    <StyledBreadcrumb key="members" label={t("organizations.members.membersPageTitle")} />
  ];

  // Render loading state
  if (loading) {
    return (
      <PageLayout breadcrumbs={breadcrumbs}>
        <Stack spacing={3}>
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
        </Stack>
      </PageLayout>
    );
  }

  // Render error state
  if (error) {
    return (
      <PageLayout breadcrumbs={breadcrumbs}>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
          onClick={handleRetry}
          data-testid="retry-button"
        >
          {t("organizations.retryButton")}
        </Button>
      </PageLayout>
    );
  }

  return (
    <PageLayout breadcrumbs={breadcrumbs} maxWidth={800}>
      <Stack spacing={3}>
        {/* Organization Info */}
        {organization && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              {organization.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("organizations.members.organizationMembersSubtitle")}
            </Typography>
          </Paper>
        )}

        {/* Add Member Form */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t("organizations.members.addMemberTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("organizations.members.addMemberSubtitle")}
          </Typography>
          
          {addMemberSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {addMemberSuccess}
            </Alert>
          )}

          {addMemberError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addMemberError}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              label={t("organizations.members.emailLabel")}
              value={newMemberEmail}
              onChange={(e) => {
                setNewMemberEmail(e.target.value);
                // Clear error when user starts typing
                if (addMemberError) {
                  setAddMemberError("");
                }
              }}
              placeholder={t("organizations.members.emailPlaceholder")}
              error={!!addMemberError}
              disabled={addingMember}
              data-testid="new-member-email-input"
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddMember}
              disabled={addingMember || !newMemberEmail.trim()}
              data-testid="add-member-button"
            >
              {addingMember ? t("organizations.members.adding") : t("add")}
            </Button>
          </Box>
        </Paper>

        {/* Members List */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t("organizations.members.membersListTitle")}
          </Typography>
          
          {membersListSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {membersListSuccess}
            </Alert>
          )}

          {membersListError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {membersListError}
            </Alert>
          )}
          
          {members.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {t("organizations.members.noMembers")}
            </Typography>
          ) : (
            <List>
              {members.map((member, index) => (
                <React.Fragment key={member.userId}>
                  <ListItem>
                    <ListItemText
                      primary={member.email || member.userId}
                      secondary={member.owner ? t("organizations.members.ownerLabel") : t("organizations.members.memberLabel")}
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t("organizations.members.ownerSwitchLabel")}
                          </Typography>
                          <Switch
                            edge="end"
                            checked={member.owner}
                            onChange={(e) => handleUpdatePermissions(member.userId, e.target.checked)}
                            disabled={member.userId === auth?.user?.sub} // Can't change own permissions
                            data-testid={`owner-switch-${member.userId}`}
                          />
                        </Box>
                        <ConfirmButton
                          onConfirm={() => handleRemoveMember(member.userId)}
                          buttonText={t("organizations.members.removeButton")}
                          confirmText={t("organizations.members.removeConfirm")}
                          cancelText={t("organizations.members.removeCancel")}
                          disabled={member.userId === auth?.user?.sub} // Can't remove yourself
                          data-testid={`remove-member-${member.userId}`}
                          confirmButtonTestId={`remove-member-confirm-${member.userId}`}
                          cancelButtonTestId={`remove-member-cancel-${member.userId}`}
                        />
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < members.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </Stack>
    </PageLayout>
  );
} 